import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, compact } from 'lodash-es';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildMMYSubmodelTrimPhotosPathWithParams, PhotosModel } from 'client/data/models/photos';
import { CATEGORIES_PLAIN } from 'site-modules/shared/constants/photoflipper/photoflipper';
import { KEY_TO_HASH } from 'site-modules/shared/utils/core-page/editorial-review';
import { getFullImageUrl, getStaticPhotoUrl, getValidImageUrl, isStaticSrc } from 'client/utils/image-helpers';
import { stripHTML } from 'site-modules/shared/utils/strip-html';
import { getEditorialRatingParams } from 'site-modules/shared/utils/core-page/core-highlights';
import { getPhotoflipperProps } from 'site-modules/core-page/utils/photo-gallery';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { EmbeddedYoutubeAsync } from 'site-modules/shared/components/video/embedded-youtube/embedded-youtube-async';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { PhotoflipperButtonAsync } from 'site-modules/shared/components/photoflipper-core/photoflipper-button/photoflipper-button-async';

const EMBED_ID_REGEX = /<embed\s+data-embed-id="(.*?)"\s*\/>/g;
// [!!sectionPhoto][isMobile]
const COLLAPSE_THRESHOLD = {
  true: {
    true: 145,
    false: 450,
  },
  false: {
    true: 145,
    false: 360,
  },
};

// [isScorecardV4]
const SECTION_PHOTO_MAP = {
  true: ['comfort'],
  false: ['interior'],
};

function RatingScorecardSectionUI({
  vehicleParams: { makeName, modelName, makeSlug, modelSlug, submodelName, year },
  name,
  sectionKey,
  rating = null,
  scale,
  content = null,
  sectionPhoto = null,
  embeds = null,
  className = null,
  scorecardVersion = null,
  isMobile = false,
  isOpen = false,
  isScorecardV4 = false,
  isLargeHeadings = false,
}) {
  const imageSize = isMobile ? 300 : 717;
  const hasCollapse = stripHTML(content).length > COLLAPSE_THRESHOLD[!!sectionPhoto][isMobile];
  const isExtendedCollapse = !isMobile && !!sectionPhoto;

  const onToggleContentCollapseState = useCallback(
    isOpenContent => {
      if (!isOpenContent) {
        EventToolbox.fireTrackAction({
          event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
          event_data: {
            action_category: TrackingConstant.USER_ACTION_CATEGORY,
            action_name: TrackingConstant.ACTION_VIEW_CONTENT,
            subaction_name: `view_scorecard_${sectionKey}`,
            creative_id: 'edm-entry-review-scorecard',
            value: name,
          },
        });
      }
    },
    [name, sectionKey]
  );

  const sectionPhotoTitle = compact([year, makeName, modelName, sectionPhoto?.trim]).join(' ');
  const sectionPhotoFragment = !!sectionPhoto && (
    <PhotoflipperButtonAsync
      tag={Fragment}
      {...getPhotoflipperProps({
        photoNum: 1,
        params: { make: makeSlug, model: modelSlug, year, submodel: submodelName },
        category: CATEGORIES_PLAIN.INTERIOR,
        isCore6981Test: true,
      })}
      dataTrackingId="view_interior_photos"
    >
      <div
        className={classnames('section-photo-wrapper pos-r no-focus', {
          'no-padding mb-1': isMobile,
          'w-100 ms-1 mb-0_5 rounded-8 float-end': !isMobile,
        })}
        style={isMobile ? undefined : { maxWidth: '300px' }}
        data-tracking-parent="scorecard-interior-photo"
        role="button"
      >
        <AspectRatioContainer className={classnames({ 'rounded-8': !isMobile })}>
          <img
            src={getValidImageUrl(sectionPhoto.href)}
            alt={sectionPhotoTitle.toLowerCase()}
            title={sectionPhotoTitle}
            loading="lazy"
          />
        </AspectRatioContainer>
        <span className="visually-hidden">View interior photos</span>
        <i className="icon-images text-white rounded-8 p-0_75 mt-0_5 me-0_5 pos-a top-0 right-0" aria-hidden />
      </div>
    </PhotoflipperButtonAsync>
  );

  const contentFragment = content.split(EMBED_ID_REGEX).map((contentChunk, index) => {
    const key = `name-${index}`;
    const isEmbed = !!(index % 2);
    const embed = isEmbed && embeds && embeds.find(({ id }) => id === contentChunk);
    let thumbnailSrc;

    if (get(embed, 'thumbnailURL')) {
      thumbnailSrc = isStaticSrc(embed.thumbnailURL)
        ? getStaticPhotoUrl(embed.thumbnailURL, isMobile)
        : getFullImageUrl(embed.thumbnailURL, imageSize);
    }

    return (
      <Fragment key={key}>
        {isEmbed ? (
          <Fragment>
            {!!get(embed, 'videoId') && (
              <div className="mb-1" data-tracking-parent={`video-short-${sectionKey}`}>
                <EmbeddedYoutubeAsync
                  videoId={embed.videoId}
                  startTime={+embed.startTime}
                  videoTitle={embed.videoTitle}
                  thumbnailSrc={thumbnailSrc}
                  creativeId={`video-${embed.videoId}`}
                  loaderClassName="m-0_25 pos-a top-0 left-0 right-0 bottom-0 border-0"
                  loaderTrackingId="vapor_video_start"
                  lazyLoadPreview
                  noPlayIcon
                />
              </div>
            )}
          </Fragment>
        ) : (
          <div className={classnames({ clearfix: !isMobile && !index && !!sectionPhoto })}>
            {!isMobile && !index && sectionPhotoFragment}
            <ContentFragment componentToUse="span" classes="review-section-fragment text-gray-darker size-16">
              {contentChunk}
            </ContentFragment>
          </div>
        )}
      </Fragment>
    );
  });

  return (
    <div
      id={KEY_TO_HASH[sectionKey]}
      className={classnames('rating-scorecard-section text-gray-darker rounded-8 bg-white', className, {
        'px-1 py-1': !isLargeHeadings,
      })}
    >
      <div
        className={classnames('d-flex gap-1', {
          'align-items-baseline mb-0_75': !isLargeHeadings,
          'align-items-center mb-0_5': isLargeHeadings,
        })}
      >
        <h2 className={classnames('fw-bold mb-0', { 'size-16': !isLargeHeadings, 'size-24': isLargeHeadings })}>
          {name}
        </h2>
        {!!rating && (
          <div
            className={classnames(
              'px-0_5 rounded-16',
              getEditorialRatingParams(rating, { isScorecardV4, scorecardVersion }).bgColorClassName,
              { medium: !isLargeHeadings }
            )}
          >
            <strong>{parseFloat(parseFloat(rating).toFixed(1))}</strong>/{scale}
          </div>
        )}
      </div>
      {isMobile && sectionPhotoFragment}
      {hasCollapse ? (
        <ContentCollapse
          key={`extended-${isExtendedCollapse}`}
          minHeight={isExtendedCollapse ? '11rem' : '4.125rem'} // 8/3 lines
          isEclipseFade={false}
          btnClasses="px-0_5 py-0 mt-0_25 size-16 text-blue-40"
          btnContainerClasses="py-0"
          justify="justify-content-start"
          showBtnBelow
          btnTextClasses="fw-normal text-underline"
          classes="size-16"
          ariaLabelHeading={`about ${name}`}
          onToggleContentCollapseState={onToggleContentCollapseState}
          trackingId={null}
          isOpen={isOpen}
        >
          {contentFragment}
        </ContentCollapse>
      ) : (
        contentFragment
      )}
    </div>
  );
}

RatingScorecardSectionUI.propTypes = {
  scale: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  sectionKey: PropTypes.string.isRequired,
  vehicleParams: PropTypes.shape({
    makeName: PropTypes.string,
    modelName: PropTypes.string,
    submodelName: PropTypes.string,
    makeSlug: PropTypes.string,
    modelSlug: PropTypes.string,
    year: PropTypes.string,
  }).isRequired,
  rating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  content: PropTypes.string,
  sectionPhoto: PropTypes.shape({
    href: PropTypes.string,
    trim: PropTypes.string,
  }),
  embeds: PropTypes.arrayOf(PropTypes.shape()),
  className: PropTypes.string,
  scorecardVersion: PropTypes.string,
  isMobile: PropTypes.bool,
  isOpen: PropTypes.bool,
  isScorecardV4: PropTypes.bool,
  isLargeHeadings: PropTypes.bool,
};

export const RatingScorecardSection = connectToModel(RatingScorecardSectionUI, {
  sectionPhoto: bindToPath(
    ({ vehicleParams, sectionKey, isScorecardV4, embedInteriorPhoto }) =>
      embedInteriorPhoto &&
      SECTION_PHOTO_MAP[!!isScorecardV4].includes(sectionKey) &&
      buildMMYSubmodelTrimPhotosPathWithParams({
        make: vehicleParams.makeSlug,
        model: vehicleParams.modelSlug,
        submodel: vehicleParams.submodelName,
        year: vehicleParams.year,
        shottype: 'default',
        provider: 'OEM,IZMO',
        photoprovider: 'OEM,IZMO',
        width: '500',
        category: CATEGORIES_PLAIN.INTERIOR,
        pageSize: 5,
      }),
    PhotosModel,
    photos => photos?.[4] // 4 in gallery, 5th - here
  ),
});
